import { DateTime } from 'luxon';
import React, { ReactElement } from 'react';


export const DATE_TIME_FORMAT = 'LLL dd, yyyy - HH:mm (ZZZZ)';

export enum TimeSourceTypeEnum {
  EPOCH_MICRO,
  EPOCH_MILLI,
  EPOCH_NANO,
  EPOCH_SECOND,
  ISO
}

export enum OutputFormatEnum {
  ARRAY,
  OBJECT,
  STRING_SINGLE
}

export type TimeFormatterArgs = {
  sourceType: TimeSourceTypeEnum;
  opts?: {
    formateStrTime?: string;
    formatStr?: string;
    formatStrDate?: string;
    useLocalZone?: boolean
  };
  outputFormat: OutputFormatEnum;
  value: string;
};

export type TimeFormatterObj = {
  date: string;
  time: string;
};

export const timeFormatter = ( args: TimeFormatterArgs ): string | ( string | ReactElement )[] | TimeFormatterObj => {
  const luxonOptions = { zone: args.opts?.useLocalZone ? 'local' : 'utc' };
  const dt: DateTime = ( () => {
    switch ( args.sourceType ) {
      case TimeSourceTypeEnum.EPOCH_SECOND:
        return DateTime.fromSeconds( Number( args.value ), luxonOptions );
      case TimeSourceTypeEnum.EPOCH_MILLI:
        return DateTime.fromMillis( Number( args.value ), luxonOptions );
      case TimeSourceTypeEnum.EPOCH_MICRO: {
        const millisMicro = Number( args.value ) / 1000;
        return DateTime.fromMillis( millisMicro, luxonOptions );
      }
      case TimeSourceTypeEnum.EPOCH_NANO: {
        const millisNano = Number( args.value ) / 1000000;
        return DateTime.fromMillis( millisNano, luxonOptions );
      }
      case TimeSourceTypeEnum.ISO:
        return DateTime.fromISO( args.value, luxonOptions );
      default:
        return DateTime.invalid( 'Unsupported sourceType' );
    }
  } )();
  const defaultFormatDate = 'LLL dd, yyyy';
  const defaultFormatTime = 'hh:mm:ss a';

  if ( args.outputFormat === OutputFormatEnum.ARRAY ) {
    return [
      dt.toFormat( args.opts?.formatStrDate || defaultFormatDate ),
      ( React.createElement( 'br', { key: 1 } ) ),
      dt.toFormat( args.opts?.formateStrTime || defaultFormatTime )
    ];
  } else if ( args.outputFormat === OutputFormatEnum.OBJECT ) {
    return {
      date: dt.toFormat( args.opts?.formatStrDate || defaultFormatDate ),
      time: dt.toFormat( args.opts?.formateStrTime || defaultFormatTime )
    };
  } else {
    return dt.toFormat( args.opts?.formatStr || DATE_TIME_FORMAT );
  }
};

/**
 * Converts a bare YYYY-MM-DD date-filter string to a full ISO datetime string
 * anchored to midnight of that day in the correct timezone.
 *
 * When useLocalTimeZone is true the offset comes from the browser's local zone
 * (e.g. "2025-02-25T00:00:00.000-07:00" for Pacific).  When false it returns a
 * UTC timestamp (e.g. "2025-02-25T00:00:00.000Z"), which matches the previous
 * behavior and keeps UTC users unaffected.
 */
export function dateFilterToISO( dateString: string, useLocalTimeZone: boolean, endOfDay = false ): string {
  let dt = DateTime.fromISO( dateString, { zone: useLocalTimeZone ? 'local' : 'utc' } );
  if ( endOfDay ) dt = dt.endOf( 'day' );
  return dt.toISO() ?? dateString;
}

export function formatISODateTime( dateString: string | undefined, useLocalTimeZone: boolean, format: string = 'LLL d, h:mm:ss a (ZZZZ)' ): string {
  if ( !dateString ) return 'N/A';
  const dt = DateTime.fromISO( dateString, { zone: 'utc' } );
  const zonedDt = useLocalTimeZone ? dt.toLocal() : dt.toUTC();
  return zonedDt.toFormat( format );
}

// Overload signatures
export function TimeFormatter( args: {
  sourceType: TimeSourceTypeEnum;
  value: string;
  outputFormat: OutputFormatEnum.STRING_SINGLE;
  opts?: TimeFormatterArgs['opts'];
} ): string;

export function TimeFormatter( args: {
  sourceType: TimeSourceTypeEnum;
  value: string;
  outputFormat: OutputFormatEnum.ARRAY;
  opts?: TimeFormatterArgs['opts'];
} ): ( string | ReactElement )[];

export function TimeFormatter( args: {
  sourceType: TimeSourceTypeEnum;
  value: string;
  outputFormat: OutputFormatEnum.OBJECT;
  opts?: TimeFormatterArgs['opts'];
} ): TimeFormatterObj;

// Implementation
export function TimeFormatter( args: {
  sourceType: TimeSourceTypeEnum;
  value: string;
  outputFormat: OutputFormatEnum;
  opts?: TimeFormatterArgs['opts'];
} ): string | ( string | ReactElement )[] | TimeFormatterObj {
  return timeFormatter( args );
}
